import { Injectable, inject } from '@angular/core';
import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';

export interface HideableColumnState {
  hidden: boolean;
}

/**
 * Clarity's `[clrDgHideableColumn]` setter calls `ColumnsService.emitStateChange()`
 * unconditionally, without comparing the previous value. Binding an inline object
 * literal (`{ hidden: column.id | hideIDColumns: hides }`) therefore creates a new
 * reference on every change detection cycle, so Clarity re-emits the state of every
 * column all the time and each `clr-dg-cell` recalculates its layout. On wide grids
 * that turns a single column reorder into seconds of blocked UI.
 *
 * This cache hands out a stable object per column so the setter only runs when the
 * hidden flag really changed. Provide it per component - the cache is grid scoped.
 */
@Injectable()
export class HideableColumnStateService {
  private readonly hideIDColumns = inject(HideIDColumnsPipe);

  private readonly states = new Map<string, HideableColumnState>();
  private readonly resolved = new Map<string, boolean>();
  private lastHides: readonly string[] | null = null;

  stateFor(columnId: string, hides: readonly string[]): HideableColumnState {
    if (hides !== this.lastHides) {
      this.lastHides = hides;
      this.resolved.clear();
    }

    let hidden = this.resolved.get(columnId);
    if (hidden === undefined) {
      hidden = this.hideIDColumns.transform(columnId, hides as string[]);
      this.resolved.set(columnId, hidden);
    }

    const cached = this.states.get(columnId);
    if (cached && cached.hidden === hidden) return cached;

    const state: HideableColumnState = { hidden };
    this.states.set(columnId, state);
    return state;
  }
}
